import { useOptionalUserWithAccount } from "context/UserAccountProvider";
import { CSSProperties, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

/**
 * CLE-2974 — the celebration when a worklist cluster is hidden ("Hide this cluster").
 *
 * CLE-2895 added a small confetti burst; Ani then asked for "the most ridiculous confetti", so this
 * is deliberately absurd: a screen flash, a giant "Good job, Ani!" pop (CLE-2980: the signed-in
 * user's first name, "Good job!" without one), and four waves of
 * confetti — a burst from the confirm button, two cannons firing up from the bottom corners, and a
 * rain from the top of the screen — in squares, strips, circles, stars and emoji, all spinning and
 * fluttering as they fall off the bottom of the viewport.
 *
 * It is still cheap: a fixed, deterministic list of spans built once per celebration, animated
 * with transform/opacity keyframes only — no per-frame JS, no canvas. It is rendered in a portal on
 * `document.body` with `pointer-events: none`, so it never blocks the dialog closing, the row
 * leaving the list or any click, it outlives the dialog, and `onDone` removes all of it once the
 * last piece has landed.
 *
 * Reduced motion: no particles, flash or banner — a small static "Good job, Ani! ✓" tag at the button
 * instead, for a little longer, then gone.
 */

// Every piece has finished (delay + duration) by this point; `buildParticles` guarantees it.
export const CELEBRATION_MS = 4200;
export const REDUCED_MOTION_CELEBRATION_MS = 1400;

const BURST_COUNT = 140;
const CANNON_COUNT = 80; // per corner
const RAIN_COUNT = 100;
export const PARTICLE_COUNT = BURST_COUNT + CANNON_COUNT * 2 + RAIN_COUNT;

// Full neon rainbow. Every colour gets a hairline edge (see `Piece`) so the light ones still
// separate from a white background, and none is dark enough to vanish on the dark theme.
const COLORS = [
  "#ef4444",
  "#f97316",
  "#facc15",
  "#a3e635",
  "#10b981",
  "#22d3ee",
  "#3b82f6",
  "#8b5cf6",
  "#d946ef",
  "#f472b6",
  "#e11d48",
  "#eab308",
];

const EMOJI = [
  "🎉",
  "🦄",
  "🌈",
  "✨",
  "💸",
  "🦖",
  "🎊",
  "🥳",
  "⭐",
  "💖",
  "🚀",
  "🍕",
  "🐸",
  "👑",
  "🔥",
  "🪩",
];

type Shape = "square" | "strip" | "circle" | "star" | "emoji";
const SHAPES: Shape[] = [
  "square",
  "strip",
  "circle",
  "strip",
  "star",
  "square",
  "emoji",
];

export interface ICelebrationOrigin {
  x: number;
  y: number;
}

export interface IViewport {
  width: number;
  height: number;
}

export interface IParticle {
  shape: Shape;
  // Where the piece starts, in viewport pixels.
  x: number;
  y: number;
  // Offsets from the start: the top of its arc, then where it lands (off the bottom of the screen).
  peakX: number;
  peakY: number;
  endX: number;
  endY: number;
  rot: number;
  flip: number;
  sway: number;
  size: number;
  color: string;
  emoji: string;
  delay: number;
  duration: number;
}

export const prefersReducedMotion = (): boolean => {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
};

/**
 * CLE-2980 — "Good job, Ani!" from the signed-in user's name. Users carry one `name` field, so the
 * first name is its first word, rendered as stored. Anything that is not a usable first name — no
 * name, blank, or an email address (some prod users have their email stored as `name`) — gives
 * "Good job!", never "undefined" or an email.
 */
export const celebrationGreeting = (
  name: string | null | undefined
): string => {
  const firstName = (name ?? "").trim().split(/\s+/)[0] ?? "";
  if (!firstName || firstName.includes("@")) return "Good job!";
  return `Good job, ${firstName}!`;
};

/** A deterministic "random" in [0, 1) from an index — same burst every time, testable. */
const noise = (index: number, salt: number): number => {
  const value = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const between = (min: number, max: number, index: number, salt: number) =>
  min + (max - min) * noise(index, salt);

/**
 * Every piece of all four waves. Pure and deterministic given the viewport and the button's
 * position, and every piece finishes (delay + duration) within CELEBRATION_MS.
 */
export const buildParticles = (
  origin: ICelebrationOrigin,
  viewport: IViewport
): IParticle[] => {
  const floor = viewport.height + 80;
  const looks = (index: number) => {
    const shape = SHAPES[index % SHAPES.length];
    return {
      shape,
      size: Math.round(
        shape === "emoji"
          ? between(24, 44, index, 1)
          : between(10, 22, index, 1)
      ),
      color: COLORS[index % COLORS.length],
      emoji: EMOJI[index % EMOJI.length],
      rot:
        (index % 2 === 0 ? 1 : -1) * Math.round(between(540, 1620, index, 2)),
      flip: Math.round(between(360, 1440, index, 3)),
      sway: Math.round(between(12, 40, index, 4)),
    };
  };

  // Wave 1: everything at once out of the confirm button, biased upward, then falling.
  const burst = Array.from({ length: BURST_COUNT }, (_, i) => {
    const index = i;
    const angle = (i / BURST_COUNT) * 2 * Math.PI + noise(index, 5) * 0.4;
    const distance = between(120, 460, index, 6);
    const peakX = Math.round(Math.cos(angle) * distance * 1.3);
    const peakY = Math.round(Math.sin(angle) * distance * 0.8 - 180);
    return {
      ...looks(index),
      x: origin.x,
      y: origin.y,
      peakX,
      peakY,
      endX: Math.round(peakX * 1.5 + between(-80, 80, index, 7)),
      endY: Math.round(floor - origin.y),
      delay: Math.round(between(0, 150, index, 8)),
      duration: Math.round(between(2300, 3000, index, 9)),
    };
  });

  // Wave 2: a cannon in each bottom corner, firing up and across the screen.
  const cannon = (side: 1 | -1, offset: number) =>
    Array.from({ length: CANNON_COUNT }, (_, i) => {
      const index = offset + i;
      const angle = between(Math.PI * 0.28, Math.PI * 0.45, index, 5);
      const distance = between(0.55, 1.05, index, 6) * viewport.height;
      const peakX = Math.round(side * Math.cos(angle) * distance);
      const peakY = Math.round(-Math.sin(angle) * distance);
      return {
        ...looks(index),
        x: side === 1 ? 0 : viewport.width,
        y: viewport.height,
        peakX,
        peakY,
        endX: Math.round(peakX * 1.4),
        endY: 80,
        delay: Math.round(between(250, 650, index, 8)),
        duration: Math.round(between(2600, 3200, index, 9)),
      };
    });

  // Wave 3: confetti rain across the whole width of the screen.
  const rain = Array.from({ length: RAIN_COUNT }, (_, i) => {
    const index = BURST_COUNT + CANNON_COUNT * 2 + i;
    const drift = between(-120, 120, index, 7);
    return {
      ...looks(index),
      x: Math.round(((i + noise(index, 5)) / RAIN_COUNT) * viewport.width),
      y: -60,
      peakX: Math.round(drift * 0.3),
      peakY: Math.round(floor * 0.2),
      endX: Math.round(drift),
      endY: floor + 60,
      delay: Math.round(between(600, 1500, index, 8)),
      duration: Math.round(between(2300, 2700, index, 9)),
    };
  });

  return [
    ...burst,
    ...cannon(1, BURST_COUNT),
    ...cannon(-1, BURST_COUNT + CANNON_COUNT),
    ...rain,
  ];
};

// Up to the top of the arc decelerating, then accelerating down like gravity.
const fly = keyframes`
  0% {
    transform: translate3d(0, 0, 0) scale(0.3);
    opacity: 1;
    animation-timing-function: cubic-bezier(0.15, 0.8, 0.35, 1);
  }
  30% {
    transform: translate3d(var(--peak-x), var(--peak-y), 0) scale(1);
    animation-timing-function: cubic-bezier(0.55, 0, 0.9, 0.55);
  }
  88% {
    opacity: 1;
  }
  100% {
    transform: translate3d(var(--end-x), var(--end-y), 0) scale(1);
    opacity: 0;
  }
`;

// Spin, tumble and side-to-side flutter, on the inner span so it composes with the flight.
const flutter = keyframes`
  0% {
    transform: translateX(0) rotate(0deg) rotateY(0deg);
  }
  25% {
    transform: translateX(var(--sway)) rotate(calc(var(--rot) * 0.25)) rotateY(calc(var(--flip) * 0.25));
  }
  50% {
    transform: translateX(0) rotate(calc(var(--rot) * 0.5)) rotateY(calc(var(--flip) * 0.5));
  }
  75% {
    transform: translateX(calc(var(--sway) * -1)) rotate(calc(var(--rot) * 0.75)) rotateY(calc(var(--flip) * 0.75));
  }
  100% {
    transform: translateX(0) rotate(var(--rot)) rotateY(var(--flip));
  }
`;

const flash = keyframes`
  0% { opacity: 0; }
  15% { opacity: 0.45; }
  100% { opacity: 0; }
`;

const pop = keyframes`
  0% { transform: translate(-50%, -50%) scale(0) rotate(-25deg); opacity: 0; }
  12% { transform: translate(-50%, -50%) scale(1.35) rotate(6deg); opacity: 1; }
  20% { transform: translate(-50%, -50%) scale(0.92) rotate(-4deg); }
  28% { transform: translate(-50%, -50%) scale(1.08) rotate(2deg); }
  36% { transform: translate(-50%, -50%) scale(1) rotate(0deg); }
  80% { transform: translate(-50%, -50%) scale(1.05) rotate(0deg); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(2.2) rotate(0deg); opacity: 0; }
`;

const Layer = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 3000;
`;

const Flash = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
  background: radial-gradient(
    circle at var(--flash-x) var(--flash-y),
    #fff7ae 0%,
    #f9a8d4 30%,
    #a5b4fc 60%,
    transparent 85%
  );
  animation: ${flash} 600ms ease-out forwards;
`;

const Banner = styled.div`
  position: absolute;
  left: 50%;
  top: 38%;
  opacity: 0;
  /* CLE-2980: a fixed width that wraps, so a long first name ("Good job, Christopher!") breaks onto
     a second line instead of running off both edges of the screen. */
  width: 92vw;
  text-align: center;
  overflow-wrap: anywhere;
  font-size: clamp(2.5rem, 9vw, 7.5rem);
  font-weight: 900;
  letter-spacing: 0.02em;
  line-height: 1;
  /* Yellow fill, purple outline and stacked pink/blue drop shadows: loud on light and dark alike. */
  color: #fde047;
  -webkit-text-stroke: 3px #6d28d9;
  text-shadow: 5px 5px 0 #db2777, 10px 10px 0 #0284c7;
  will-change: transform, opacity;
  animation: ${pop} 2800ms ease-out 150ms forwards;
`;

const BannerEmoji = styled.div`
  margin-top: 0.75rem;
  font-size: clamp(1.5rem, 4vw, 3rem);
  -webkit-text-stroke: 0;
  text-shadow: none;
`;

const Flight = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  will-change: transform, opacity;
  animation-name: ${fly};
  animation-fill-mode: forwards;
`;

const Piece = styled.span`
  display: block;
  line-height: 1;
  will-change: transform;
  animation-name: ${flutter};
  animation-timing-function: linear;
  animation-fill-mode: forwards;

  &[data-shape="square"],
  &[data-shape="strip"] {
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
  }

  &[data-shape="circle"] {
    border-radius: 50%;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
  }

  &[data-shape="star"] {
    clip-path: polygon(
      50% 0%,
      61% 35%,
      98% 35%,
      68% 57%,
      79% 91%,
      50% 70%,
      21% 91%,
      32% 57%,
      2% 35%,
      39% 35%
    );
  }
`;

const MarkedTag = styled.span`
  position: absolute;
  transform: translate(-50%, -150%);
  white-space: nowrap;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: var(--green-50, #f0fdf4);
  color: var(--green-800, #166534);
  border: 1px solid var(--green-200, #bbf7d0);
`;

const pieceStyle = (particle: IParticle): CSSProperties => {
  const { shape, size } = particle;
  const timing = {
    animationDuration: `${particle.duration}ms`,
    animationDelay: `${particle.delay}ms`,
  };
  if (shape === "emoji") {
    return { ...timing, fontSize: size, marginLeft: -size / 2 };
  }
  const width = shape === "strip" ? Math.round(size * 0.45) : size;
  const height = shape === "strip" ? Math.round(size * 1.6) : size;
  return {
    ...timing,
    width,
    height,
    marginLeft: -width / 2,
    marginTop: -height / 2,
    background: particle.color,
  };
};

export function WorklistCelebration({
  origin,
  onDone,
}: {
  origin: ICelebrationOrigin;
  onDone: () => void;
}) {
  const reduced = prefersReducedMotion();
  const greeting = celebrationGreeting(
    useOptionalUserWithAccount()?.userInfo?.name
  );
  const particles = useMemo(
    () =>
      reduced
        ? []
        : buildParticles(origin, {
            width: window.innerWidth,
            height: window.innerHeight,
          }),
    [origin, reduced]
  );

  useEffect(() => {
    const timer = window.setTimeout(
      onDone,
      reduced ? REDUCED_MOTION_CELEBRATION_MS : CELEBRATION_MS + 150
    );
    return () => window.clearTimeout(timer);
  }, [onDone, reduced]);

  return createPortal(
    <Layer
      data-testid="benefitWorklist.dismiss.celebration"
      aria-hidden={!reduced}
    >
      {reduced ? (
        <MarkedTag
          role="status"
          data-testid="benefitWorklist.dismiss.marked"
          style={{ left: origin.x, top: origin.y }}
        >
          {greeting} ✓
        </MarkedTag>
      ) : (
        <>
          <Flash
            style={
              {
                "--flash-x": `${origin.x}px`,
                "--flash-y": `${origin.y}px`,
              } as CSSProperties
            }
          />
          {particles.map((particle, index) => (
            <Flight
              // A fixed list, never reordered.
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              data-testid="benefitWorklist.dismiss.particle"
              style={
                {
                  left: particle.x,
                  top: particle.y,
                  animationDuration: `${particle.duration}ms`,
                  animationDelay: `${particle.delay}ms`,
                  "--peak-x": `${particle.peakX}px`,
                  "--peak-y": `${particle.peakY}px`,
                  "--end-x": `${particle.endX}px`,
                  "--end-y": `${particle.endY}px`,
                } as CSSProperties
              }
            >
              <Piece
                data-shape={particle.shape}
                style={
                  {
                    ...pieceStyle(particle),
                    "--rot": `${particle.rot}deg`,
                    "--flip": `${particle.flip}deg`,
                    "--sway": `${particle.sway}px`,
                  } as CSSProperties
                }
              >
                {particle.shape === "emoji" ? particle.emoji : null}
              </Piece>
            </Flight>
          ))}
          <Banner data-testid="benefitWorklist.dismiss.banner">
            {greeting}
            <BannerEmoji>🎉🦄🌈✨💸🦖🎊</BannerEmoji>
          </Banner>
        </>
      )}
    </Layer>,
    document.body
  );
}
